import { Typography, Badge } from '@lucid/lego';
import type { Order } from '../../../../api/services/lupp/order/dto.order';
import { formatCurrency, formatDate } from '../../../../utils/tools';

/**
 * Get badge color based on order status
 * @param status - Order status string
 * @returns Badge color variant
 */
export const getStatusBadgeColor = (status: string) => {
  if (!status) return 'gray';

  switch (status.toLowerCase()) {
    case 'awaiting assignment':
      return 'orange';
    case 'in production':
    case 'production':
      return 'darktaupe';
    case 'factory gated':
      return 'darktaupe';
    case 'in transit to eu fpr':
    case 'in transit':
      return 'green';
    case 'vehicle at eu fpr':
    case 'vehicle at port': // Legacy support
      return 'blue';
    case 'vehicle ready for dispatch':
      return 'blue';
    case 'vehicle dispatched':
      return 'green';
    case 'vehicle on-site':
      return 'blue';
    case 'delivered':
      return 'gray';
    default:
      return 'gray';
  }
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  onEditClick: (record: Order) => void;
  labels: Record<string, string>;
  locale: string;
};

/**
 * Helper to create a simple text column
 */
const createTextColumn = (
  key: keyof Order,
  title: string,
  width: string,
  getValue?: (record: Order) => string
) => ({
  key,
  title,
  width,
  sortable: true,
  render: (_: unknown, record: Order) => (
    <Typography variant="body" size="small" color="black">
      {getValue ? getValue(record) : record[key] || '-'}
    </Typography>
  ),
});

/**
 * Helper to create a clickable link column
 */
const createLinkColumn = (
  key: keyof Order,
  title: string,
  width: string,
  theme: 'light' | 'dark',
  onEditClick: (record: Order) => void,
  getValue?: (record: Order) => string
) => ({
  key,
  title,
  width,
  sortable: true,
  render: (_: unknown, record: Order) => (
    <Typography
      variant="body"
      size="small"
      color="link"
      theme={theme}
      onClick={() => onEditClick(record)}
    >
      {getValue ? getValue(record) : record[key] || '-'}
    </Typography>
  ),
});

export const createColumns = ({ theme, onEditClick, labels, locale }: CreateColumnsParams) => [
  {
    key: 'statusName',
    title: labels?.status || 'Status',
    width: '150px',
    align: 'left' as const,
    sortable: true,
    render: (_: unknown, record: Order) => {
      const status = record.statusName?.toLowerCase() || 'awaiting assignment';
      const statusLabelMap: Record<string, string> = {
        'awaiting assignment':
          labels?.['const.ORDER_STATUS.awaitingAssignment'] ?? 'Awaiting assignment',
        'in production': labels?.['const.ORDER_STATUS.inProduction'] ?? 'In Production',
        production: labels?.['const.ORDER_STATUS.production'] ?? 'Production',
        'factory gated': labels?.['const.ORDER_STATUS.factoryGated'] ?? 'Factory Gated',
        'in transit to eu fpr':
          labels?.['const.ORDER_STATUS.inTransitToEuFpr'] ?? 'In Transit to EU FPR',
        'in transit': labels?.['const.ORDER_STATUS.inTransit'] ?? 'In Transit',
        'vehicle at eu fpr': labels?.['const.ORDER_STATUS.vehicleAtEuFpr'] ?? 'Vehicle at EU FPR',
        'vehicle at port': labels?.['const.ORDER_STATUS.vehicleAtPort'] ?? 'Vehicle at Port', // Legacy support
        'vehicle ready for dispatch':
          labels?.['const.ORDER_STATUS.vehicleReadyForDispatch'] ?? 'Vehicle ready for dispatch',
        'vehicle dispatched':
          labels?.['const.ORDER_STATUS.vehicleDispatched'] ?? 'Vehicle dispatched',
        'vehicle on-site': labels?.['const.ORDER_STATUS.vehicleOnSite'] ?? 'Vehicle On-site',
        delivered: labels?.['const.ORDER_STATUS.delivered'] ?? 'Delivered',
      };
      const statusLabel =
        statusLabelMap[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
      return (
        <Badge
          label={statusLabel}
          color={getStatusBadgeColor(status)}
          type="secondary"
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  createLinkColumn(
    'orderNumber',
    labels?.orderNumber || 'Order #',
    '150px',
    theme,
    onEditClick,
    (record) => record.orderNumber || '-'
  ),
  createTextColumn('vin', labels?.vin || 'VIN', '160px'),
  createTextColumn('batchNumber', labels?.batch || 'Batch', '120px'),
  createTextColumn('customerType', labels?.customerType || 'Customer Type', '140px'),
  createTextColumn('invoiceToCompanyName', labels?.companyName || 'Company Name', '180px'),
  createLinkColumn(
    'registerToCustomerFullName',
    labels?.fullName || 'Full Name',
    '160px',
    theme,
    onEditClick
  ),
  {
    key: 'customerReadinessDate',
    title: labels?.['customer.deliveryTimeline'] || 'Customer Delivery Timeline',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.customerReadinessDate)}
      </Typography>
    ),
  },
  {
    key: 'vehicleReadyToScheduleDate',
    title: labels?.vehicleReadyToScheduleDate || 'Vehicle Ready to Schedule Date',
    width: '220px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.vehicleReadyToScheduleDate)}
      </Typography>
    ),
  },
  {
    key: 'scheduledDeliveryDate',
    title: labels?.scheduledDeliveryDate || 'Scheduled Delivery Date',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.scheduledDeliveryDate)}
      </Typography>
    ),
  },
  {
    key: 'orderTotal',
    title: labels?.orderTotal || 'Order Total',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatCurrency(record.orderTotal, locale)}
      </Typography>
    ),
  },
  {
    key: 'totalAmountReceived',
    title: labels?.totalAmountReceived || 'Total Amount Received',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatCurrency(record.totalAmountReceived, locale)}
      </Typography>
    ),
  },
  {
    key: 'totalAmountDue',
    title: labels?.amountDue || 'Amount Due (Prior to Delivery)',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Order) => (
      <Typography variant="body" size="small" color="black">
        {formatCurrency(record.totalAmountDue, locale)}
      </Typography>
    ),
  },
  createTextColumn(
    'assignedOwnerName',
    labels?.assignedOwner || 'Assigned Owner (Agent Name)',
    '160px'
  ),
];
